import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

type FigureProps = {
  last: number;
  number: number;
  mini: boolean;
  darkDigits?: boolean;
};

type Props = {
  // Allow external styling
  className?: string;
  // Number of seconds left on the timer
  startDuration: number;
  // Only show minutes and seconds if hours are '00'
  hideHours?: boolean;
  mini?: boolean;
  style?: object;
  darkIndicator?: boolean;
  darkDigits?: boolean;
};

const NumberContainer = styled.div<{ mini: boolean; darkDigits: boolean }>`
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  border-radius: 2.5px;
  color: #0954d3;
  font-size: 38px;
  font-weight: 500;
  height: 45px;
  perspective: 130px;
  position: relative;
  width: 40px;
  line-height: 25px;

  &:not(:last-of-type) {
    margin-right: 4px;
  }

  &.mini {
    height: 30px;
    width: 30px;
    font-size: 22px;
    line-height: 18px;
  }

  &.darkDigits {
    color: black;
  }
`;

const NumberHalf = styled.div<{
  bottom?: boolean;
  top?: boolean;
  topBack?: boolean;
  topBehind?: boolean;
}>`
  background: #fff;
  display: grid;
  height: 50%;
  left: 0;
  overflow: hidden;
  place-items: center;
  position: absolute;
  right: 0;

  &.bottom {
    border-bottom-left-radius: 2.5px;
    border-bottom-right-radius: 2.5px;
    bottom: 0;
    z-index: 0;
  }

  &.top {
    background: #f9f9f9;
    backface-visibility: hidden;
    border-top-left-radius: 2.5px;
    border-top-right-radius: 2.5px;
    top: 0;
    transform-origin: bottom;
    z-index: 3;
  }

  &.top::after,
  &.topBack::after {
    border-bottom: 1px solid #ddd;
    bottom: 0;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    width: 100%;
    z-index: -1;
  }

  &.topBack {
    border-top-left-radius: 2.5px;
    border-top-right-radius: 2.5px;
    height: 50%;
    top: 0;
    transform-origin: bottom;
    z-index: 2;
  }

  &.topBehind {
    background: #f9f9f9;
    border-top-left-radius: 2.5px;
    border-top-right-radius: 2.5px;
    height: 50%;
    top: 0;
    z-index: 1;
  }
`;

const NumberText = styled.div<{
  bottom?: boolean;
  top?: boolean;
  topBack?: boolean;
}>`
  position: absolute;
  left: 0;
  margin: auto;
  right: 0;
  text-align: center;

  &.top {
    top: 50%;
  }

  &.topBack {
    // This makes this half line up with where the bottom half
    // should be. Idk why it's 60% but visually it's the correct value.
    top: 60%;
    transform: scaleY(-1);
  }

  &.bottom {
    top: -50%;
  }
`;

const Group = styled.div`
  display: flex;
`;

const Indicator = styled.p<{ mini: boolean; dark: boolean }>`
  color: #fff;
  font-weight: 500;
  font-size: 10px;
  letter-spacing: 0.16em;
  margin: 0;
  text-align: center;
  text-transform: uppercase;

  &.mini {
    font-size: 8px;
  }

  &.dark {
    color: #4b4b4b; /* Design system's grayDarkest */
  }
`;

const StaticNumberContainer = styled.div<{ darkDigits: boolean }>`
  background: #fff;
  border-radius: 2.5px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  color: #0954d3;
  display: grid;
  font-size: 38px;
  font-weight: 500;
  height: 45px;
  perspective: 130px;
  place-items: center;
  position: relative;
  width: 40px;

  &:not(:last-of-type) {
    margin-right: 4px;
  }

  &::after {
    content: '';
    height: 1px;
    background: #ddd;
    left: 0;
    position: absolute;
    top: 50%;
    width: 100%;
    z-index: -1;
  }
  &.darkDigits {
    color: black;
  }
`;

const TimerContainer = styled.div<{ withoutHours: boolean; mini: boolean }>`
  display: grid;
  gap: 3px 13px;
  grid-template-columns: repeat(3, auto);
  justify-content: center;
  // Helps vertical alignment, the numbers below the digits
  // throw it off a bit, this helps recenter it
  padding-top: 16px;

  &.withoutHours {
    grid-template-columns: repeat(2, auto);
  }

  &.mini {
    gap: 3px 8px;
  }
`;

const DURATION = 500;

const ROTATE_KEYFRAMES = [
  { transform: 'rotateX(0)' },
  { transform: 'rotateX(-180deg)' },
];

/**
 * Return the individual digits for hours, minutes, and numbers
 * given the duration in seconds.
 */
const getDigitsFromDuration = (duration: number): number[] => {
  const hours = Math.floor(duration / 3600);
  let time = duration - hours * 3600;
  const mins = Math.floor(time / 60);
  time = time - mins * 60;
  const secs = time % 60;

  const hs = String(hours).split('').map(Number);
  const ms = String(mins).split('').map(Number);
  const ss = String(secs).split('').map(Number);

  // If we only have one number, we are at single digits
  // in that place and we need to add a leading 0
  if (hs.length === 1) hs.unshift(0);
  if (ms.length === 1) ms.unshift(0);
  if (ss.length === 1) ss.unshift(0);

  return [...hs, ...ms, ...ss];
};

// Safari doesn't support `backface-visibility`, it needs
// `-webkit-backface-visibility`.
// Safari has an obnoxious flickering bug that affects the whole page
// because it is a bad browser that can't handle transform animations correctly.
// `-webkit-backface-visibility: hidden` in the right spots
// fixes the flickering bug, but this animation needs the backface
// to be visibile on certain elements to look correct.
// This just makes sure we only animate in good browsers that don't suck.
// SSR-safe: Check if CSS is defined before using it (not available on server)
const useAnimation =
  typeof CSS !== 'undefined' && CSS.supports('backface-visibility: hidden');

// `last` is needed to show the correct number on the front,
// `number` is the number on the back side of the cards
// Using React.PureComponent here since we rerender the timer every second,
// but usually only one or two digits change at a time,
// so this should help with performance.
// Initially, this was a function component, but due to the way
// useEffect/useState interact, there was a pretty consistent jitter every render
// as the new number would show very briefly, then it would switch to the `last`
// number, animate, then show the new number again, before repeating on the next
// tick of the timer.
// Using a class component with componentDidUpdate is uglier, but gives us finer
// control over when state updates, and allows us to avoid those "inbetween" renders
// that were causing the flickering.
class AnimatedFigure extends React.PureComponent<FigureProps> {
  topRef: HTMLDivElement | null = null;
  topBackRef: HTMLDivElement | null = null;

  state = {
    useNext: false,
  };

  componentDidUpdate(prevProps: FigureProps) {
    const { number } = this.props;
    const { number: prevNumber } = prevProps;

    if (prevNumber === number) return;

    this.setState({ useNext: false }, () => {
      const topAnim = this.topRef?.animate(ROTATE_KEYFRAMES, {
        duration: DURATION,
        easing: 'ease-in-out',
      }).finished;
      const topBackAnim = this.topBackRef?.animate(ROTATE_KEYFRAMES, {
        duration: DURATION,
        easing: 'ease-in-out',
      }).finished;

      Promise.all([topAnim, topBackAnim]).then(() => {
        this.setState({ useNext: true });
      });
    });
  }

  render() {
    const { last, number, mini, darkDigits = false } = this.props;
    const { useNext } = this.state;

    return (
      <NumberContainer mini={mini} darkDigits={darkDigits}>
        {/* These two halves are the ones that flip */}
        <NumberHalf ref={(el) => (this.topRef = el)} top>
          <NumberText top>{useNext ? number : last}</NumberText>
        </NumberHalf>
        <NumberHalf ref={(el) => (this.topBackRef = el)} topBack>
          <NumberText topBack>{number}</NumberText>
        </NumberHalf>

        {/* These don't flip */}
        <NumberHalf topBehind>
          <NumberText top>{number}</NumberText>
        </NumberHalf>
        <NumberHalf bottom>
          <NumberText bottom>{useNext ? number : last}</NumberText>
        </NumberHalf>
      </NumberContainer>
    );
  }
}

const StaticFigure = ({ number, darkDigits = false }: FigureProps) => (
  <StaticNumberContainer darkDigits={darkDigits}>
    {number}
  </StaticNumberContainer>
);

const Figure = useAnimation ? AnimatedFigure : StaticFigure;

const Timer: React.FC<Props> = ({
  className,
  startDuration,
  hideHours,
  mini,
  style,
  darkIndicator,
  darkDigits = false,
}) => {
  // Because of the way the animation works, we actually want to start
  // one second sooner.
  // We're dealing with a discount campaign here, not nuclear reactors,
  // so I don't think being off by a second is that big of a deal.
  const [duration, setDuration] = useState(startDuration - 1);

  // If our startDuration changes, we want to reset the timer
  useEffect(() => {
    setDuration(startDuration);
  }, [startDuration]);

  useEffect(() => {
    const id = setInterval(() => {
      setDuration((d) => {
        if (d === 0) {
          clearInterval(id);
          return d;
        }

        return d - 1;
      });
    }, 1000);

    return () => {
      clearInterval(id);
    };
  }, []);

  const [h1, h2, m1, m2, s1, s2] = getDigitsFromDuration(duration);
  const [h1last, h2last, m1last, m2last, s1last, s2last] =
    getDigitsFromDuration(duration + 1);

  // Hide hours when all hour digits are zero and hideHours is true
  const hideHoursFromTimer = !!(hideHours && !h1 && !h2 && !h1last && !h2last);

  return (
    <TimerContainer
      className={className}
      withoutHours={hideHoursFromTimer}
      mini={!!mini}
      style={{ ...style }}
    >
      {!hideHoursFromTimer && (
        <Group>
          <Figure
            last={h1last}
            number={h1}
            mini={!!mini}
            darkDigits={darkDigits}
          />
          <Figure
            last={h2last}
            number={h2}
            mini={!!mini}
            darkDigits={darkDigits}
          />
        </Group>
      )}
      <Group>
        <Figure
          last={m1last}
          number={m1}
          mini={!!mini}
          darkDigits={darkDigits}
        />
        <Figure
          last={m2last}
          number={m2}
          mini={!!mini}
          darkDigits={darkDigits}
        />
      </Group>
      <Group>
        <Figure
          last={s1last}
          number={s1}
          mini={!!mini}
          darkDigits={darkDigits}
        />
        <Figure
          last={s2last}
          number={s2}
          mini={!!mini}
          darkDigits={darkDigits}
        />
      </Group>

      {!hideHoursFromTimer && (
        <Indicator mini={!!mini} dark={!!darkIndicator}>
          Hours
        </Indicator>
      )}
      <Indicator mini={!!mini} dark={!!darkIndicator}>
        Minutes
      </Indicator>
      <Indicator mini={!!mini} dark={!!darkIndicator}>
        Seconds
      </Indicator>
    </TimerContainer>
  );
};

export default Timer;
